import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Play, Sparkles, FileText, Tag, Zap, Clock } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Three synthetic "processing" stages the client animates when the
// user hits Save. The API call itself is instant (starters are pre-
// processed) — the animation is what makes the interaction *feel*
// like real AI work and turns a first-run save into a habitual save.
const PROCESSING_STAGES = [
    { label: 'Transcribing audio', icon: Zap, ms: 1500 },
    { label: 'Summarising with AI', icon: Sparkles, ms: 1600 },
    { label: 'Categorising & tagging', icon: Tag, ms: 1100 },
];

/**
 * Facade YouTube embed — renders a thumbnail with a play button on
 * initial mount and only swaps in a real `<iframe>` on click. This
 * keeps first paint fast (no 5x iframe boot for 5 cards) while still
 * giving the user in-place playback. Same pattern as
 * lite-youtube-embed, hand-rolled to avoid a dependency.
 */
function YouTubeFacade({ videoId, title, thumb }) {
    const [playing, setPlaying] = useState(false);
    // hqdefault.jpg is the only YouTube thumbnail size guaranteed to
    // return a real image for every public video. maxresdefault
    // silently 200s with a 1KB grey placeholder for older uploads
    // (e.g. Steve Jobs Stanford 2005), so we don't touch it.
    const thumbUrl = thumb || `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;

    if (playing) {
        return (
            <div className="relative w-full aspect-video bg-black rounded-xl overflow-hidden">
                <iframe
                    className="absolute inset-0 w-full h-full"
                    src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`}
                    title={title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                />
            </div>
        );
    }

    return (
        <button
            type="button"
            onClick={() => setPlaying(true)}
            className="group relative w-full aspect-video bg-zinc-900 rounded-xl overflow-hidden cursor-pointer block"
            aria-label={`Play ${title}`}
            data-testid={`starter-embed-play-${videoId}`}
        >
            <img
                src={thumbUrl}
                alt=""
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/40" />
            {/* Real YouTube-style play button (red pill) */}
            <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-16 h-11 sm:w-20 sm:h-14 rounded-xl bg-[#ff0000] group-hover:bg-[#ff0000] flex items-center justify-center shadow-2xl transition-all group-hover:scale-110">
                    <Play className="w-6 h-6 sm:w-7 sm:h-7 text-white fill-white ml-0.5" />
                </div>
            </div>
        </button>
    );
}

function StarterRow({ starter, savedBookmarkId, onSaved }) {
    // 'idle' → 'processing' → 'done'. Persists across remounts via
    // the parent-tracked `savedBookmarkId`.
    const [status, setStatus] = useState(savedBookmarkId ? 'done' : 'idle');
    const [stageIndex, setStageIndex] = useState(0);
    const timers = useRef([]);

    useEffect(() => {
        if (savedBookmarkId && status !== 'done') setStatus('done');
    }, [savedBookmarkId, status]);

    useEffect(() => () => timers.current.forEach(clearTimeout), []);

    const handleSave = async () => {
        if (status !== 'idle') return;
        setStatus('processing');
        setStageIndex(0);

        // Fire the API call immediately — server returns the fully-
        // formed bookmark in one round-trip since starters are pre-
        // processed. The animation runs in parallel client-side.
        const apiPromise = fetch(`${API}/bookmarks/starter/${starter.id}`, {
            method: 'POST',
            credentials: 'include',
        }).then((r) => (r.ok ? r.json() : Promise.reject(r)));

        try {
            for (let i = 0; i < PROCESSING_STAGES.length; i++) {
                setStageIndex(i);
                await new Promise((res) => {
                    const t = setTimeout(res, PROCESSING_STAGES[i].ms);
                    timers.current.push(t);
                });
            }
            const data = await apiPromise;
            setStatus('done');
            toast.success(`Saved — "${starter.title.split(' — ')[0].split(':')[0]}" is in your library.`);
            onSaved?.(starter.id, data.bookmark);
        } catch (err) {
            console.error('Starter save failed', err);
            toast.error('Could not save that one. Try again in a moment.');
            setStatus('idle');
        }
    };

    return (
        <motion.article
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="rounded-2xl bg-white border border-border overflow-hidden shadow-sm hover:shadow-md transition-shadow"
            data-testid={`starter-row-${starter.id}`}
        >
            <div className="grid md:grid-cols-[minmax(0,1.2fr),minmax(0,1fr)]">
                {/* Video embed - always keeps the 16:9 aspect ratio */}
                <div className="p-4 sm:p-5 md:p-6 md:pr-3">
                    <YouTubeFacade
                        videoId={starter.youtube_id}
                        title={starter.title}
                        thumb={starter.thumbnail}
                    />
                </div>

                {/* Metadata + Save */}
                <div className="p-4 pt-1 sm:p-5 sm:pt-1 md:p-6 md:pl-3 flex flex-col">
                    <div className="flex items-center gap-2 mb-2">
                        {starter.hero_cta && (
                            <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-widest bg-primary/10 text-primary">
                                {starter.hero_cta}
                            </span>
                        )}
                        {starter.duration_label && (
                            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground">
                                <Clock className="w-3 h-3" />
                                {starter.duration_label}
                            </span>
                        )}
                    </div>

                    <p className="text-[10px] font-semibold uppercase tracking-widest text-emerald-700 mb-1.5">
                        {starter.category} · {starter.author}
                    </p>
                    <h3 className="font-heading text-lg sm:text-xl font-semibold text-foreground leading-snug tracking-tight mb-2">
                        {starter.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed flex-1 line-clamp-3 md:line-clamp-4">
                        {starter.description}
                    </p>

                    {/* Save button + inline processing UI */}
                    <div className="mt-4 min-h-[52px]">
                        <AnimatePresence mode="wait">
                            {status === 'idle' && (
                                <motion.button
                                    key="idle"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    type="button"
                                    onClick={handleSave}
                                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-xl bg-primary hover:bg-primary/90 text-white font-semibold text-sm py-2.5 px-5 transition-colors shadow-sm"
                                    data-testid={`starter-row-${starter.id}-save`}
                                >
                                    <FileText className="w-4 h-4" />
                                    Save to my BrainSync
                                </motion.button>
                            )}

                            {status === 'processing' && (
                                <motion.div
                                    key="processing"
                                    initial={{ opacity: 0, y: 4 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0 }}
                                    className="rounded-xl bg-emerald-950/95 text-white px-4 py-3 shadow-lg"
                                    data-testid={`starter-row-${starter.id}-processing`}
                                >
                                    <div className="flex flex-col gap-1.5">
                                        {PROCESSING_STAGES.map((stage, i) => {
                                            const Icon = stage.icon;
                                            const isDone = i < stageIndex;
                                            const isCurrent = i === stageIndex;
                                            return (
                                                <div
                                                    key={stage.label}
                                                    className={`flex items-center gap-2 text-[12.5px] transition-all ${isDone ? 'text-emerald-200' : isCurrent ? 'text-white' : 'text-emerald-100/40'}`}
                                                >
                                                    <span className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${isDone ? 'bg-emerald-500' : isCurrent ? 'bg-emerald-600 animate-pulse' : 'border border-emerald-800'}`}>
                                                        {isDone
                                                            ? <Check className="w-2.5 h-2.5" strokeWidth={3} />
                                                            : <Icon className={`w-2.5 h-2.5 ${isCurrent ? 'text-white' : 'text-emerald-600/60'}`} />}
                                                    </span>
                                                    <span className="font-medium">{stage.label}</span>
                                                    {isCurrent && (
                                                        <span className="ml-auto text-emerald-300 tracking-widest text-[10px]">...</span>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </motion.div>
                            )}

                            {status === 'done' && (
                                <motion.div
                                    key="done"
                                    initial={{ opacity: 0, scale: 0.96 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 font-semibold text-sm py-2.5 px-5"
                                    data-testid={`starter-row-${starter.id}-done`}
                                >
                                    <span className="w-5 h-5 rounded-full bg-emerald-600 flex items-center justify-center">
                                        <Check className="w-3 h-3 text-white" strokeWidth={3} />
                                    </span>
                                    Saved to your library
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>
            </div>
        </motion.article>
    );
}

export default function StarterVideos({ onSaved, headerless = false }) {
    const [starters, setStarters] = useState([]);
    const [saved, setSaved] = useState({});  // {starter_id: bookmark_id}
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(`${API}/bookmarks/starter`, { credentials: 'include' });
                if (!res.ok) return;
                const data = await res.json();
                if (cancelled) return;
                setStarters(data.starters || []);
                setSaved(data.saved || {});
            } catch (err) {
                console.error('Failed to load starters', err);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const handleSaved = (starterId, bookmark) => {
        setSaved((prev) => ({ ...prev, [starterId]: bookmark?.bookmark_id }));
        onSaved?.(bookmark, starterId);
    };

    if (loading || starters.length === 0) return null;

    return (
        <section className={headerless ? '' : 'mt-10 sm:mt-14'} data-testid="starter-videos">
            {!headerless && (
                <div className="mb-5 sm:mb-6">
                    <h3 className="font-heading text-xl sm:text-2xl font-semibold text-foreground tracking-tight">
                        Or start with one of these
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                        Hand-picked videos, already summarised, transcribed and categorised. Watch here, then one-tap adds them to your library.
                    </p>
                </div>
            )}
            <div className="flex flex-col gap-4 sm:gap-5">
                {starters.map((s) => (
                    <StarterRow
                        key={s.id}
                        starter={s}
                        savedBookmarkId={saved[s.id]}
                        onSaved={handleSaved}
                    />
                ))}
            </div>
        </section>
    );
}
